import React, { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import Head from 'next/head'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useSelector, useDispatch } from 'react-redux'
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js'
import { RootState } from '@/app/store'
import { dialogRegisterQuestionnaireState } from '@/features/dialogRegisterQuestionnaireSlice'
import { jwttokenState, availableState } from '@/features/authSlice'
import awsConfiguration from '@/lib/awsconfiguration'
import Layout from '@/components/Layout'
import RegisterQuestionnaire from '@/components/RegisterQuestionnaire'
import Auth from '@/components/Auth'
import Input from '@/components/Input'
import Button from '@/components/Button'
import { urlaccount, urlquestionnairesanswer } from '@/lib/custom'
import Loading from '@/components/Loading'
import { accountState, passwordState } from '@/features/accountSlice'

type Inputs = {
  username: string
  postalcode: string
  address: string
  password: string
  account: string
  mail?: any
}

const SignIn = () => {
  const dispatch = useDispatch()
  const router = useRouter()
  const [signinState, setSigninState] = useState<string | undefined>('')

  const availableStateSelector = useSelector(
    (availableState: RootState) => availableState.authAvailable.available
  )

  const loginTypeStateSelector = useSelector(
    (loginTypeState: RootState) => loginTypeState.loginType.loginType
  )

  const accountSelector = useSelector(
    (state: RootState) => state.account.account
  )

  const passwordSelector = useSelector(
    (state: RootState) => state.account.password
  )

  const dialogRegisterQuestionnaireStateSelector = useSelector(
    (dialogRegisterQuestionnaireState: RootState) =>
      dialogRegisterQuestionnaireState.dialogRegisterQuestionnaireState.boolean
  )

  useEffect(() => {
    if (localStorage.getItem(`${process.env.NEXT_PUBLIC_SERVICE_NAME}-jwt`)) {
      if (loginTypeStateSelector !== 9) {
        router.push({
          pathname: '/',
        })
      }
    }
    return
  }, [loginTypeStateSelector])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>()

  const onSubmitDialog: SubmitHandler<Inputs> = async (data) => {
    dispatch(availableState(true))

    try {
      const authAccoutFetch = await fetch(
        `${urlaccount}?username=${data.account}`,
        {
          headers: {
            'X-Raund-Token': `${process.env.NEXT_PUBLIC_RAUND_TOKEN}`,
          },
        }
      )

      const authAccoutFetchResult = await authAccoutFetch.json()

      const { user } = authAccoutFetchResult

      // ユーザー認証
      const authenticationDetails = new AuthenticationDetails({
        Username: user.account,
        Password: data.password,
      })

      // ユーザープール
      const userPool = new CognitoUserPool({
        UserPoolId: awsConfiguration.UserPoolId,
        ClientId: awsConfiguration.ClientId,
      })

      // ユーザーを定義
      const cognitoUser = new CognitoUser({
        Username: user.account,
        Pool: userPool,
      })

      // ユーザーセッションを確立させるプロセス
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          const cognitoUser01 = userPool.getCurrentUser()
          cognitoUser01.getSession((err, session) => {
            if (err) {
              alert(err.message || JSON.stringify(err))
              return
            }

            // @ts-ignore
            window.session = session
            localStorage.setItem(
              `${process.env.NEXT_PUBLIC_SERVICE_NAME}-jwt`,
              session.getAccessToken().getJwtToken()
            )

            localStorage.setItem(
              `${process.env.NEXT_PUBLIC_SERVICE_NAME}-exp-token`,
              session.accessToken.payload.exp
            )

            dispatch(jwttokenState(true))

            const Questionnaires = async () => {
              try {
                const authAuestionnairesFetch = await fetch(
                  `${urlquestionnairesanswer}/1`,
                  {
                    headers: {
                      'X-Authorization': `${session
                        .getAccessToken()
                        .getJwtToken()}`,
                      'X-Raund-Token': `${process.env.NEXT_PUBLIC_RAUND_TOKEN}`,
                    },
                  }
                )
                const result = await authAuestionnairesFetch.json()

                if (result.status === 404) {
                  dispatch(availableState(false))
                  location.href = `${process.env.NEXT_PUBLIC_SITEURL}/${process.env.NEXT_PUBLIC_SERVICE_NAME}/`
                }

                if (result.code === 'answered') {
                  dispatch(availableState(false))
                  location.href = `${process.env.NEXT_PUBLIC_SITEURL}/${process.env.NEXT_PUBLIC_SERVICE_NAME}/`
                }

                if (result.code === 'unanswered') {
                  dispatch(accountState(user.account))
                  dispatch(passwordState(data.password))
                  dispatch(dialogRegisterQuestionnaireState(false))
                }
              } catch (error) {
                console.log(error)
              }
            }

            Questionnaires()
          })
        },
        onFailure: (err) => {
          setSigninState('アカウント名とパスワードが登録されていません。')
          dispatch(availableState(false))
        },
      })

      return
    } catch (response) {
      setSigninState('アカウント名とパスワードが登録されていません。')
      dispatch(availableState(false))
    }
  }

  return (
    <Layout title="">
      <Head>
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0,minimum-scale=1.0,maximum-scale=1.0,user-scalable=no"
        />
      </Head>

      <Auth title="ログイン">
        {signinState !== '' ? (
          <div className="questerror text-sm">{`${signinState}`}</div>
        ) : (
          <></>
        )}

        <form className="" method="" onSubmit={handleSubmit(onSubmitDialog)}>
          <fieldset>
            {loginTypeStateSelector === 1 ? (
              <div className="relative">
                <Input
                  labelname="アカウント名"
                  htmlfor="account"
                  type="text"
                  placeholder=""
                  register={register}
                  required
                  require={true}
                  arialabel="account"
                />
                {errors.account?.type === 'required' && (
                  <p className="error text-xss text-color01">
                    ※入力してください
                  </p>
                )}
              </div>
            ) : (
              ''
            )}

            {loginTypeStateSelector === 2 ? (
              <div className="relative">
                <Input
                  labelname="メールアドレス"
                  htmlfor="account"
                  type="text"
                  placeholder=""
                  register={register}
                  validate={{
                    m: (v) =>
                      (v &&
                        /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/.test(
                          v
                        )) ||
                      '※メール形式で入力してください。',
                  }}
                  required
                  require={true}
                  arialabel="account"
                />
                {errors.account?.type === 'm' && (
                  <p className="error text-xs text-color01">
                    {errors.account?.message}
                  </p>
                )}
                {errors.account?.type === 'required' && (
                  <p className="error text-xss text-color01">
                    ※入力してください
                  </p>
                )}
              </div>
            ) : (
              ''
            )}
            <div className="relative mt-6">
              <Input
                labelname="パスワード"
                htmlfor="password"
                type="password"
                placeholder=""
                register={register}
                minLength={8}
                validate={{
                  p: (v) =>
                    (v && /[0-9]/.test(v)) || '※半角数字を含めてください',
                  e: (v) =>
                    (v && /[a-z]/.test(v)) || '※半角小文字を含めてください',
                }}
                required
                require={true}
                arialabel="password"
              />

              {errors.password?.type === 'e' && (
                <p className="error text-xs text-color01">
                  {errors.password?.message}
                </p>
              )}
              {errors.password?.type === 'p' && (
                <p className="error text-xs text-color01">
                  {errors.password?.message}
                </p>
              )}
              {errors.password?.type === 'minLength' && (
                <p className="error text-xs text-color01">
                  ※8桁以上でご入力ください。
                </p>
              )}
              {errors.password?.type === 'required' && (
                <p className="error text-xs text-color01">※入力してください</p>
              )}
            </div>
          </fieldset>

          <Button text="ログイン" type="submit" arialabel="submit" />

          <div className="mt-6 text-center text-sm sm:mb-4 sm:flex sm:flex-wrap">
            <Link href="/auth/register">
              <a className="flex-2">初めての方はこちら</a>
            </Link>
          </div>

          <div className="mt-6 text-center text-sm sm:mb-4 sm:flex sm:flex-wrap">
            <Link href="https://raund.net/terms/" passHref={true}>
              <a className="flex-2" target="_new" rel="noopener">
                利用規約はこちら
              </a>
            </Link>
          </div>
        </form>
      </Auth>
      {availableStateSelector ? <Loading /> : ''}
      {!dialogRegisterQuestionnaireStateSelector ? (
        <RegisterQuestionnaire
          accountState={{
            account: accountSelector,
            password: passwordSelector,
          }}
        />
      ) : (
        ''
      )}
    </Layout>
  )
}

export default SignIn
